import { authClient } from "@/auth-client";
import { SYSTEM_ROLE_LABELS } from "@/components/settings/company-settings";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { sessionQueryOptions } from "@/lib/query-options";
import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Ban,
  Loader2,
  LogOut,
  MoreHorizontal,
  Trash2,
  Undo,
  UserCheck,
  UserCog,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

// --- Interfaces (Updated based on usage) ---
// Infer this type directly or define based on actual API response
type UserFromApi = {
  id: string;
  email: string;
  name: string;
  role: string;
  banned: boolean;
  emailVerified: boolean;
  createdAt: string;
};

// --- Query Options ---

// Users Query (Updated based on usage)
const usersAdminQueryOptions = queryOptions({
  queryKey: ["admin", "users"],
  queryFn: async () => {
    const { data, error } = await authClient.admin.listUsers({
      query: {
        sortBy: "createdAt",
        sortDirection: "desc",
      },
    });
    if (error) {
      // Log the actual error for debugging
      console.error("API Error fetching users:", error);
      throw new Error(`Failed to fetch users: ${error.message}`);
    }
    if (!data) {
      // Handle cases where data might be unexpectedly null/undefined
      throw new Error("No data received when fetching users");
    }
    return data; // Return the whole object { users: UserFromApi[] }
  },
});

// --- Route Definition ---

export const Route = createFileRoute(
  "/(dashboard)/_authenticated/admin/_guard/users",
)({
  loader: ({ context: { queryClient } }) => {
    return Promise.all([
      queryClient.ensureQueryData(sessionQueryOptions),
      queryClient.ensureQueryData(usersAdminQueryOptions),
    ]);
  },
  pendingComponent: () => (
    <div className="p-4 border rounded-md flex items-center space-x-2 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span>Cargando usuarios...</span>
    </div>
  ),
  component: UsersComponent,
});

// --- Main Admin Component ---

function UsersComponent() {
  const { data: usersData } = useSuspenseQuery(usersAdminQueryOptions);
  const { data: sessionData } = useSuspenseQuery(sessionQueryOptions);
  const currentUserId = sessionData?.user?.id;

  const queryClient = useQueryClient();
  const [userToRemove, setUserToRemove] = useState<UserFromApi | null>(null);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState("");

  const removeUserMutation = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const { error } = await authClient.admin.removeUser({ userId });
      if (error) {
        console.error("API Error removing user:", error);
        throw new Error(`Failed to remove user: ${error.message}`);
      }
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({
        queryKey: usersAdminQueryOptions.queryKey,
      });
      toast.success(`Usuario (${userId}) eliminado correctamente.`);
      closeDeleteDialog();
    },
    onError: (error) => {
      toast.error(error.message || "Error al eliminar el usuario.");
      closeDeleteDialog();
    },
  });

  const setRoleMutation = useMutation({
    mutationFn: async ({
      userId,
      role,
    }: { userId: string; role: "admin" | "user" }) => {
      const { error } = await authClient.admin.setRole({ userId, role });
      if (error) {
        console.error("API Error setting role:", error);
        throw new Error(`Failed to set role: ${error.message}`);
      }
    },
    onSuccess: (_, { userId, role }) => {
      queryClient.invalidateQueries({
        queryKey: usersAdminQueryOptions.queryKey,
      });
      toast.success(
        `Rol del usuario (${userId}) establecido como ${SYSTEM_ROLE_LABELS[role]}.`,
      );
    },
    onError: (error) => {
      toast.error(error.message || "Error al establecer el rol.");
    },
  });

  const banUserMutation = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const { error } = await authClient.admin.banUser({
        userId,
        banReason: "Administrative action",
        banExpiresIn: 60 * 60 * 24 * 7, // 7 days in seconds
      });
      if (error) {
        console.error("API Error banning user:", error);
        throw new Error(`Failed to ban user: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: usersAdminQueryOptions.queryKey,
      });
      toast.success("Usuario bloqueado correctamente.");
    },
    onError: (error) => {
      toast.error(error.message || "Error al bloquear el usuario.");
    },
  });

  const unbanUserMutation = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const { error } = await authClient.admin.unbanUser({ userId });
      if (error) {
        console.error("API Error unbanning user:", error);
        throw new Error(`Failed to unban user: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: usersAdminQueryOptions.queryKey,
      });
      toast.success("Usuario desbloqueado correctamente.");
    },
    onError: (error) => {
      toast.error(error.message || "Error al desbloquear el usuario.");
    },
  });

  const revokeSessionsMutation = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const { error } = await authClient.admin.revokeUserSessions({ userId });
      if (error) {
        console.error("API Error revoking sessions:", error);
        throw new Error(`Failed to revoke sessions: ${error.message}`);
      }
    },
    onSuccess: () => {
      toast.success("Sesiones del usuario revocadas correctamente.");
    },
    onError: (error) => {
      toast.error(error.message || "Error al revocar las sesiones.");
    },
  });

  const impersonateUserMutation = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const { error } = await authClient.admin.impersonateUser({ userId });
      if (error) {
        console.error("API Error impersonating user:", error);
        throw new Error(`Failed to impersonate user: ${error.message}`);
      }
    },
    onSuccess: () => {
      toast.success("Suplantación iniciada correctamente. Recargando...");
      // Reload the page to reflect the impersonation
      window.location.reload();
    },
    onError: (error) => {
      toast.error(error.message || "Error al suplantar el usuario.");
    },
  });

  const openDeleteDialog = (user: UserFromApi) => {
    setUserToRemove(user);
    setDeleteConfirmationInput("");
  };

  const closeDeleteDialog = () => {
    setUserToRemove(null);
    setDeleteConfirmationInput("");
  };

  const confirmRemoveUser = () => {
    if (userToRemove && deleteConfirmationInput === "delete") {
      removeUserMutation.mutate({ userId: userToRemove.id });
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex gap-2 mb-2">
        <Link to="/" className="inline-block">
          <Button variant="outline">Inicio</Button>
        </Link>
        <Link to="/admin" className="inline-block">
          <Button variant="outline">Panel administrador</Button>
        </Link>
      </div>
      <div>
        <h3 className="text-lg font-medium">Gestión de usuarios</h3>
        <p className="text-sm text-muted-foreground">
          Gestiona las cuentas de usuario y permisos.
        </p>
      </div>

      <div className="relative w-full overflow-auto">
        <Table>
          <TableCaption>Lista de usuarios registrados.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px]">Email</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Verificado</TableHead>
              <TableHead>Bloqueado</TableHead>
              <TableHead>Creado</TableHead>
              <TableHead className="text-right min-w-[80px]">
                Acciones
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usersData && usersData.users.length > 0 ? (
              usersData.users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    {user.email}
                    {user.id === currentUserId && (
                      <Badge variant="outline" className="ml-2">
                        Tú
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{user.name || "-"}</TableCell>
                  <TableCell>
                    {SYSTEM_ROLE_LABELS[
                      user.role as keyof typeof SYSTEM_ROLE_LABELS
                    ] || SYSTEM_ROLE_LABELS.user}
                  </TableCell>
                  <TableCell>{user.emailVerified ? "Sí" : "No"}</TableCell>
                  <TableCell>{user.banned ? "Sí" : "No"}</TableCell>
                  <TableCell>
                    {new Date(user.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Abrir menú</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() =>
                            setRoleMutation.mutate({
                              userId: user.id,
                              role: user.role === "admin" ? "user" : "admin",
                            })
                          }
                          disabled={setRoleMutation.isPending}
                        >
                          <UserCog className="mr-2 h-4 w-4" />
                          {user.role === "admin"
                            ? "Convertir a usuario"
                            : "Convertir a admin"}
                        </DropdownMenuItem>
                        {!user.banned ? (
                          <DropdownMenuItem
                            onClick={() =>
                              banUserMutation.mutate({ userId: user.id })
                            }
                            disabled={banUserMutation.isPending}
                          >
                            <Ban className="mr-2 h-4 w-4" />
                            Bloquear usuario
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() =>
                              unbanUserMutation.mutate({ userId: user.id })
                            }
                            disabled={unbanUserMutation.isPending}
                          >
                            <Undo className="mr-2 h-4 w-4" />
                            Desbloquear usuario
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() =>
                            revokeSessionsMutation.mutate({ userId: user.id })
                          }
                          disabled={revokeSessionsMutation.isPending}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          Revocar todas las sesiones
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            impersonateUserMutation.mutate({ userId: user.id })
                          }
                          disabled={
                            impersonateUserMutation.isPending ||
                            user.id === currentUserId
                          }
                        >
                          <UserCheck className="mr-2 h-4 w-4" />
                          Suplantar usuario
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                          onClick={() =>
                            openDeleteDialog({
                              id: user.id,
                              email: user.email,
                              name: user.name,
                              role: user.role ?? ("user" as const),
                              banned: user.banned ?? false,
                              emailVerified: user.emailVerified ?? false,
                              createdAt: user.createdAt.toISOString(),
                            })
                          }
                          disabled={user.id === currentUserId}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No se encontraron usuarios.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Remove User Dialog */}
      <AlertDialog open={!!userToRemove} onOpenChange={closeDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás absolutamente seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              la cuenta de usuario ({userToRemove?.email}).
              <br />
              <span className="font-semibold text-foreground mt-2 block">
                Por favor, escriba "delete" para confirmar.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            id="delete-confirm"
            value={deleteConfirmationInput}
            onChange={(e) => setDeleteConfirmationInput(e.target.value)}
            autoComplete="off"
            disabled={removeUserMutation.isPending}
          />
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={closeDeleteDialog}
              disabled={removeUserMutation.isPending}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmRemoveUser}
              disabled={
                deleteConfirmationInput !== "delete" ||
                removeUserMutation.isPending
              }
            >
              {removeUserMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
